import CloseIcon from '@material-ui/icons/Close';
import momentTimezone from 'moment-timezone';
import React, { useEffect, useState } from 'react';
import {
  DateTimeInput,
  EditContextProvider,
  Loading,
  required,
  SaveButton,
  SelectInput,
  SimpleForm,
  Toolbar,
  useEditController,
  useQuery,
} from 'react-admin';
import Modal from 'react-modal';
import { Link } from 'react-router-dom';
import { customDataProvider } from '../data-provider';
import {
  filterLocationOfferedAddons,
  getAppointmentDateTime,
  getAppointmentLocationId,
  getCatalogAddonPrice,
  getDateTime,
  getInitialAddons,
  getNewPaidCatalogAddons,
  isBreakroom,
  isTherapist,
  showToast,
} from '../utils';
import { APPOINTMENT_STATUS } from '../utils/constants';
import { isEmpty } from 'lodash';
import { FormSpy } from 'react-final-form';
import { AppointmentAddonCheckboxGroup } from './appointment-addon-input';
import { buildAddonChoicesWithCombinationState } from '../utils/addon-combination-rules';

const validateService = [required()];
const validateTherapist = [required()];

const formatPriceForDisplay = amount => {
  const n = Number(amount);
  if (!Number.isFinite(n) || n <= 0) return '0';
  return n % 1 === 0 ? String(n) : n.toFixed(2);
};

export const AppointmentEdit = ({
  appointment,
  callBack,
  permissions,
  setShowLoader,
  selectedLocation,
  setFetchData,
}) => {
  const therapistEditOnly = isTherapist(permissions);
  const [showModal, setShowModal] = useState(false);
  const [showAddonConfirmModal, setShowAddonConfirmModal] = useState(false);
  const [addonsToConfirm, setAddonsToConfirm] = useState([]);
  const [formData, setFormdata] = useState();
  const [showAddon, setShowAddon] = useState(true);
  const [addonChangeType, setAddonChangeType] = useState(null);

  const controllerProps = useEditController({
    basePath: `/appointments/${appointment.id}`,
    permissions: 'admin',
    resource: 'appointment',
    id: appointment.id,
  });
  const appointmentData = controllerProps.record;
  const appointmentLocationId = getAppointmentLocationId(
    appointmentData || appointment,
    selectedLocation,
  );

  const { data: services, loading: servicesLoading } = useQuery({
    type: 'getList',
    resource: 'services',
    payload: { location_id: appointmentLocationId },
  });

  const { data: addons, loading: addonsLoading } = useQuery({
    type: 'getList',
    resource: 'service-addons',
    payload: { locationId: appointmentLocationId },
  });

  const locationOfferedAddons = filterLocationOfferedAddons(
    addons,
    appointmentLocationId,
  );

  const getInitialAddonIds = () =>
    getInitialAddons(appointmentData || appointment);

  useEffect(() => {
    setShowAddon(locationOfferedAddons.length > 0);
  }, [locationOfferedAddons.length]);

  const handleUpdateAppoitnment = (form, includeAddon) => {
    const { service, therapist, slotTime, addon } = form || formData || {};
    const addonIds = includeAddon ? addon || [] : getInitialAddonIds();

    const payload = therapistEditOnly
      ? {
          ...(!isEmpty(addonChangeType) && {
            addon_service_ids: includeAddon && showAddon ? addonIds : [],
          }),
        }
      : {
          service_id: service?.id ?? appointment?.service?.id,
          therapist_id: therapist?.id ?? appointment?.therapist?.id,
          slot_time: getDateTime(slotTime, true),
          ...(!isEmpty(addonChangeType) && {
            addon_service_ids: includeAddon && showAddon ? addonIds : [],
          }),
        };

    if (therapistEditOnly && isEmpty(addonChangeType)) {
      showToast('No add-on changes to save.', 'error');
      return;
    }
    setShowLoader(true);

    customDataProvider
      .update('appointment', { id: appointment?.id, action: 'edit', payload })
      .then(() => {
        if (showAddon && includeAddon) {
          if (addonChangeType === 'added') {
            showToast('Success! The add-on has been added.');
          } else if (addonChangeType === 'removed') {
            showToast('Success! The add-on has been removed.');
          }
        }
        setFetchData(true);
        callBack && callBack();
        setShowLoader(false);
      })
      .catch(() => {
        setShowLoader(false);
        setFetchData(false);
      });
  };

  const closeAddonConfirmModal = () => {
    setShowAddonConfirmModal(false);
    setAddonsToConfirm([]);
  };

  const handleSave = form => {
    if (
      appointment?.no_show !== 0 ||
      appointment.status === APPOINTMENT_STATUS.CANCELLED ||
      isEmpty(form)
    ) {
      return;
    }

    const { addon } = form;
    setFormdata(form);

    const initialIds = getInitialAddonIds();
    const addedIds = (addon || []).filter(id => !initialIds.includes(id));
    const removedIds = initialIds.filter(id => !(addon || []).includes(id));

    if (addedIds.length) {
      setAddonChangeType('added');
    } else if (removedIds.length) {
      setAddonChangeType('removed');
    } else {
      setAddonChangeType(null);
    }

    const newPaidAddons = getNewPaidCatalogAddons(
      addon,
      initialIds,
      locationOfferedAddons,
      locationOfferedAddons,
    );

    if (newPaidAddons.length) {
      setAddonsToConfirm(newPaidAddons);
      setShowAddonConfirmModal(true);
      return;
    }

    handleUpdateAppoitnment(form, true);
  };

  const EditAppointmentToolbar = props => (
    <Toolbar {...props}>
      <SaveButton label="Save" onSave={handleSave} />
    </Toolbar>
  );

  const getActiveServices = () => {
    const activeServices = services?.filter(s => s.title !== 'mini');
    return activeServices?.map(s => ({
      ...s,
      name: s.label,
    }));
  };

  const modifiedTherapists = () => {
    const therapistsList = JSON.parse(localStorage.getItem('therapistsList'));
    return therapistsList?.map(t => ({
      ...t,
      name: `${t.first_name} ${t.last_name}`,
    }));
  };

  const handleAppointmentUpdate = action => {
    setShowLoader(true);
    customDataProvider
      .update('appointment', {
        id: appointment?.id,
        action: action,
        payload: {},
      })
      .then(() => {
        setFetchData(true);
        callBack && callBack();
        setShowLoader(false);
      })
      .catch(() => {
        setShowLoader(false);
        setFetchData(false);
      });
  };

  const apptTime = momentTimezone
    .utc(appointment?.start_at)
    .tz(appointment?.timezone)
    .format('YYYY-MM-DDTHH:mm');

  const noShowModal = () => {
    const header = 'Mark No-show';
    const buttonText = 'Yes';
    const bodyContent = 'Are you sure you want to no show this appointment?';
    return (
      <Modal isOpen={showModal}>
        <div className="modal-header">
          <div className="light-text">{header}</div>
          <CloseIcon
            onClick={() => setShowModal(false)}
            className="close-button"
          />
        </div>
        <div className="btn-padding">{bodyContent}</div>
        <div className="details-display btn-padding">
          <div>
            <button
              className="button-blue button-red"
              onClick={() => setShowModal(false)}
            >
              No
            </button>
          </div>
          <div align="right">
            <button
              className="button-blue"
              onClick={() => {
                setShowModal(false);
                handleAppointmentUpdate('noshow');
              }}
            >
              {buttonText}
            </button>
          </div>
        </div>
      </Modal>
    );
  };

  const handleAddonSelection = selectedAddonIds => {
    const initialIds = getInitialAddonIds();
    const added = (selectedAddonIds || []).filter(id => !initialIds.includes(id));
    const removed = initialIds.filter(id => !(selectedAddonIds || []).includes(id));

    if (added.length) {
      setAddonChangeType('added');
    } else if (removed.length) {
      setAddonChangeType('removed');
    } else {
      setAddonChangeType(null);
    }
  };

  const handleTherapistChange = () => {
    setShowAddon(locationOfferedAddons.length > 0);
  };

  const buildAddonConfirmCopy = () => {
    const hasMembership = appointment?.user?.has_membership;
    const chargeSuffix =
      ' If they have Squeeze Bucks, they will be used. If not, it will be charged to their credit card on file.';

    if (addonsToConfirm.length === 1) {
      const addon = addonsToConfirm[0];
      const label = addon?.addon_type?.label || 'add-on';
      const price = formatPriceForDisplay(
        getCatalogAddonPrice(addon, hasMembership),
      );
      return {
        header: `Add ${label} to Appointment`,
        body: `If you add ${label} to the appointment, the guest will be charged $${price}.${chargeSuffix}`,
      };
    }

    const labels = addonsToConfirm
      .map(a => a?.addon_type?.label)
      .filter(Boolean)
      .join(', ');
    const total = addonsToConfirm.reduce(
      (sum, addon) => sum + getCatalogAddonPrice(addon, hasMembership),
      0,
    );
    const price = formatPriceForDisplay(total);

    return {
      header: 'Add add-ons to Appointment',
      body: `If you add the following add-ons (${labels}) to the appointment, the guest will be charged $${price}.${chargeSuffix}`,
    };
  };

  const addonConfirmPopupModal = () => {
    if (!addonsToConfirm.length) return null;

    const { header, body } = buildAddonConfirmCopy();

    return (
      <Modal isOpen={!!showAddonConfirmModal}>
        <div className="modal-header">
          <div className="light-text">{header}</div>
          <CloseIcon
            onClick={closeAddonConfirmModal}
            className="close-button"
          />
        </div>
        <div className="btn-padding">{body}</div>
        <div className="details-display btn-padding">
          <div>
            <button
              className="button-blue button-red"
              onClick={closeAddonConfirmModal}
            >
              NEVERMIND
            </button>
          </div>
          <div align="right">
            <button
              className="button-blue"
              onClick={() => {
                closeAddonConfirmModal();
                handleUpdateAppoitnment(formData, true);
              }}
            >
              CHARGE GUEST
            </button>
          </div>
        </div>
      </Modal>
    );
  };

  if (!appointmentData || servicesLoading || addonsLoading) {
    return <Loading />;
  }
  return (
    <>
      {noShowModal()}
      {addonConfirmPopupModal()}
      <EditContextProvider value={controllerProps} style={{ width: '100%' }}>
        <div className="details-display grid4060">
          <div className="font-bold">Date/Time</div>
          {
            <div className="break-all">
              {isBreakroom(permissions) ? (
                <span>
                  {getAppointmentDateTime(
                    appointment?.start_at,
                    appointment?.end_at,
                    appointment?.timezone,
                  )}
                </span>
              ) : (
                <Link
                  to={`/guest/appointment/${appointment?.id}/view-guest-details/${appointment?.user_id}`}
                  className="underline"
                >
                  {getAppointmentDateTime(
                    appointment?.start_at,
                    appointment?.end_at,
                    appointment?.timezone,
                  )}
                </Link>
              )}
            </div>
          }
          <div className="font-bold">Guest</div>
          {
            <div className="break-all">
              {isBreakroom(permissions) || therapistEditOnly ? (
                <div>
                  {appointment?.user?.first_name}{' '}
                  {therapistEditOnly
                    ? appointment?.user?.last_name?.charAt(0)
                    : appointment?.user?.last_name}
                </div>
              ) : (
                <>
                  <Link
                    className="underline"
                    to={`/guest/${appointment?.user_id}`}
                  >
                    {appointment?.user?.first_name}{' '}
                    {appointment?.user?.last_name}
                  </Link>
                </>
              )}
            </div>
          }
          {!therapistEditOnly &&
            appointment?.status === APPOINTMENT_STATUS.CONFIRMED && (
              <>
                <div className="font-bold">Mark as No Show</div>
                <div>
                  <button
                    className="button-blue mr-6 button-red"
                    onClick={() => setShowModal(true)}
                  >
                    No Show
                  </button>
                </div>
              </>
            )}
        </div>
        <SimpleForm
          initialValues={{
            service: appointment?.service?.id,
            therapist: appointment?.therapist?.id,
            slotTime: apptTime,
            addon: getInitialAddonIds(),
          }}
          toolbar={<EditAppointmentToolbar />}
        >
          {!therapistEditOnly && (
            <>
              <DateTimeInput
                source="slotTime"
                optionText="name"
                label="Start Time"
                style={{ width: '100%' }}
              />
              <SelectInput
                source="service.id"
                optionText="name"
                choices={getActiveServices()}
                label="Service"
                style={{ width: '100%' }}
                validate={validateService}
              />
              <SelectInput
                source="therapist.id"
                choices={modifiedTherapists()}
                label="Therapist"
                style={{ width: '100%' }}
                validate={validateTherapist}
                onChange={e => handleTherapistChange(e.target.value)}
              />
            </>
          )}
          {locationOfferedAddons.length > 0 && showAddon ? (
            <FormSpy subscription={{ values: true }}>
              {({ values }) => {
                const selectedAddonIds = Array.isArray(values?.addon)
                  ? values.addon
                  : [];
                const addonChoicesWithState = buildAddonChoicesWithCombinationState(
                  locationOfferedAddons,
                  selectedAddonIds,
                  null,
                  {
                    hasMembership: !!appointment?.user?.has_membership,
                  },
                );

                return (
                  <AppointmentAddonCheckboxGroup
                    label="Addons"
                    source="addon"
                    choices={addonChoicesWithState}
                    onSelectionChange={handleAddonSelection}
                  />
                );
              }}
            </FormSpy>
          ) : therapistEditOnly ? (
            <div className="text-sm text-gray-600 py-2">
              No add-ons are available for this therapist at this location.
            </div>
          ) : (
            ''
          )}
        </SimpleForm>
      </EditContextProvider>
    </>
  );
};
